import * as React from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import classNames from 'classnames';
import { capitalize } from 'lodash';
import { Breadcrumbs, IconButton, Typography } from '@material-tailwind/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import ContentPlaceholder from '../ContentPlaceholder';
import Backdrop from '../Backdrop';
import Button from 'src/app/ui/Button';
import useContentSummary from '../../hooks/useContentSummary';

export interface CardData {
  id: string | number;
  title: string;
  type: string;
  public_image: string;
  short_description: string;
  external_source?: string;
  external_source_id?: string;
  description: string;
  name: string;
  rating: string;
  origin: string;
  has_summary?: boolean;
}

interface Props extends CardData {
  isSelected?: boolean;
  onOpenDetails?: () => void;
  onRemove?: (id: string | number) => void;
}

const YOUTUBE_SOURCE_PATTERN = /\b(?:youtube|youtu\.be|yt)\b/i;
const APP_LANGUAGE_KEY = 'appLanguage';

const StyledQuickSummaryModal = styled(motion.div).attrs({
  className:
    'container relative mx-auto my-8 max-h-[calc(100vh-4rem)] overflow-y-auto rounded-2xl bg-dark-600 py-8 flex flex-col items-start',
})``;

const dropIn = {
  hidden: {
    y: '70vh',
    opacity: 0,
  },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      bounce: 0.1,
      duration: 0.6,
    },
  },
  exit: {
    y: '100vh',
    opacity: 0,
    transition: {
      type: 'spring',
      bounce: 0,
      duration: 1,
    },
  },
};

const QuickSummaryFeatureImage = ({
  image,
  type,
}: {
  image?: string;
  type?: string;
}) => {
  const hasImage = !!image;

  return (
    <div className="w-64 max-w-full rounded-md shadow-md shadow-dark transition-all">
      <motion.div
        className={classNames({
          'relative flex h-40 w-full flex-col items-center rounded-lg shadow-md shadow-blue-gray-500/10':
            true,
        })}
      >
        {hasImage ? (
          <div
            style={{
              backgroundImage: `url(${image})`,
            }}
            className="absolute inset-0 rounded-lg bg-center bg-cover bg-gray-600"
          />
        ) : (
          <ContentPlaceholder
            type={type || 'generic'}
            size="hero"
            className="absolute inset-0 rounded-lg"
          />
        )}

        <motion.div
          layout="position"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="bg-dark-600/90 z-10 mt-4 ml-2 w-max self-start rounded px-4 py-2 shadow-lg shadow-dark backdrop-blur-sm"
        >
          <motion.span className="text-sm text-label-500">
            {type || 'Contenido'}
          </motion.span>
        </motion.div>
      </motion.div>
    </div>
  );
};

const QuickSummaryActions = ({
  name,
  handleClose,
  language,
}: {
  name: string;
  handleClose: () => void;
  language: 'es' | 'en';
}) => {
  const copy =
    language === 'en'
      ? {
          summary: 'Summary',
        }
      : {
          summary: 'Resumen',
        };

  return (
    <div className="flex w-full items-start justify-between px-8">
      <Breadcrumbs
        className="mt-2 bg-transparent text-light-100"
        separator={
          <svg
            aria-hidden="true"
            className="h-6 w-6 text-light-100"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        }
      >
        <a className="opacity-80">Wiki</a>
        <a className="opacity-80">{copy.summary}</a>
        <a className="font-bold">{name}</a>
      </Breadcrumbs>

      <IconButton
        className="rounded-full border-white/10 focus:ring-black/50"
        variant="outlined"
        color="blue-gray"
        onClick={handleClose}
      >
        <XMarkIcon strokeWidth={2} className="h-5 w-5" />
      </IconButton>
    </div>
  );
};

const LoadingState = ({ language }: { language: 'es' | 'en' }) => {
  return (
    <div className="flex min-h-[220px] w-full flex-col items-center justify-center gap-5">
      <div className="h-12 w-12 animate-spin rounded-full border-4 border-white/15 border-t-primary-500" />
      <Typography variant="paragraph" className="text-center text-white/80">
        {language === 'en'
          ? 'Preparing the highlights...'
          : 'Preparando los aspectos destacados...'}
      </Typography>
    </div>
  );
};

const QuickSummaryBody = ({
  title,
  type,
  image,
  isLoading,
  error,
  summary,
  onOpenDetails,
  language,
}: {
  title: string;
  type: string;
  image?: string;
  isLoading: boolean;
  error: boolean;
  summary: string | null | undefined;
  onOpenDetails: () => void;
  language: 'es' | 'en';
}) => {
  const copy =
    language === 'en'
      ? {
          error: 'Could not load the summary.',
          unavailable: 'Summary not available.',
          title: 'Highlights from this content',
          details: 'View details',
        }
      : {
          error: 'No se ha podido cargar el resumen.',
          unavailable: 'Resumen no disponible.',
          title: 'Aspectos destacados de este contenido',
          details: 'Ver detalle',
        };

  return (
    <div className="w-full p-4">
      <div className="my-4 flex w-full flex-col gap-8">
        <div className="flex w-full flex-col items-start gap-6 md:flex-row md:items-center">
          <QuickSummaryFeatureImage image={image} type={type} />

          <div className="flex w-full flex-col items-start justify-center gap-3 md:min-h-[10rem]">
            <Typography variant="h3">{title}</Typography>
          </div>
        </div>

        <div className="w-full">
          <Typography variant="h6">{copy.title}</Typography>

          <div className="mt-3 min-h-[220px] w-full rounded-2xl border border-white/10 bg-black/10 p-5">
            {isLoading && <LoadingState language={language} />}

            {!isLoading && error && (
              <Typography variant="paragraph" className="text-red-300">
                {copy.error}
              </Typography>
            )}

            {!isLoading && !error && !!summary?.trim() && (
              <Typography variant="paragraph" className="whitespace-pre-line">
                {summary}
              </Typography>
            )}

            {!isLoading && !error && !summary?.trim() && (
              <Typography variant="paragraph" className="text-white/60">
                {copy.unavailable}
              </Typography>
            )}
          </div>
        </div>

        <div className="flex w-full justify-end">
          <Button primary type="button" onClick={onOpenDetails}>
            {copy.details}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const Card = (props: Props) => {
  const navigate = useNavigate();
  const language =
    localStorage.getItem(APP_LANGUAGE_KEY) === 'en' ? 'en' : 'es';
  const copy =
    language === 'en'
      ? {
          summaryButton: 'View summary',
          remove: 'Remove',
        }
      : {
          summaryButton: 'Ver resumen',
          remove: 'Quitar',
        };

  const hasImage = !!props.public_image;
  const sourceLabel = props.external_source || props.origin || '';
  const isYoutubeContent = YOUTUBE_SOURCE_PATTERN.test(sourceLabel);
  // Only YouTube content gets summaries, and the backend says which ones have
  // one. A missing `has_summary` (older backend) hides the button.
  const isQuickSummaryAvailable =
    isYoutubeContent && props.has_summary === true;

  const [isQuickSummaryOpen, setIsQuickSummaryOpen] = React.useState(false);

  // Fetched only once the modal opens; react-query keeps it for reopenings.
  const {
    data: summaryText,
    isLoading: isQuickSummaryLoading,
    isError: isQuickSummaryError,
  } = useContentSummary(
    props.id,
    isQuickSummaryAvailable && isQuickSummaryOpen
  );

  const handleCloseQuickSummary = React.useCallback(() => {
    setIsQuickSummaryOpen(false);
  }, []);

  const handleGoToDetails = React.useCallback(() => {
    handleCloseQuickSummary();
    navigate(`/explorer/${props.id}`);
  }, [handleCloseQuickSummary, navigate, props.id]);

  const handleQuickSummaryClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isQuickSummaryAvailable) {
      return;
    }

    setIsQuickSummaryOpen(true);
  };

  const handleRemoveClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    props.onRemove?.(props.id);
  };

  const quickSummaryModal =
    isQuickSummaryAvailable && isQuickSummaryOpen
      ? createPortal(
          <Backdrop onClick={handleCloseQuickSummary}>
            <StyledQuickSummaryModal
              variants={dropIn}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(event) => event.stopPropagation()}
            >
              <QuickSummaryActions
                name={props.name}
                handleClose={handleCloseQuickSummary}
                language={language}
              />

              <QuickSummaryBody
                title={props.name}
                type={props.type}
                image={props.public_image}
                isLoading={isQuickSummaryLoading}
                error={isQuickSummaryError}
                summary={summaryText}
                onOpenDetails={handleGoToDetails}
                language={language}
              />
            </StyledQuickSummaryModal>
          </Backdrop>,
          document.body
        )
      : null;

  return (
    <>
      <div>
        <motion.div
          className={classNames({
            'relative flex h-48 flex-col items-center rounded-lg shadow-md shadow-blue-gray-500/10':
              true,
          })}
        >
          {hasImage ? (
            <div
              style={{
                backgroundImage: `url(${props.public_image})`,
              }}
              className="absolute inset-0 rounded-lg bg-center bg-cover bg-gray-600"
            />
          ) : (
            <ContentPlaceholder
              type={props.type}
              className="absolute inset-0 rounded-lg"
            />
          )}

          <span className="absolute left-2 top-2 rounded-lg bg-black/50 p-2 text-xs shadow-md drop-shadow-md">
            {capitalize(
              props.type ? props.type.split('_').join(' ') : 'Generic'
            )}
          </span>

          {props.origin && (
            <span className="absolute right-2 top-2 rounded-lg bg-black/50 p-2 text-xs shadow-md drop-shadow-md">
              {props.external_source
                ? capitalize(props.external_source)
                : capitalize(props.origin)}
            </span>
          )}
        </motion.div>

        <motion.h2 className="mb-0 mt-3 text-lg font-bold">
          {props.name}
        </motion.h2>
        <motion.p className="my-2 text-sm text-white/60">
          {props.short_description}
        </motion.p>

        <div className="mt-3 flex items-end justify-between gap-3">
          <motion.div className="text-xs font-bold text-white/60">
            {props.rating}
          </motion.div>

          <div className="flex items-center gap-2">
            {isQuickSummaryAvailable && (
              <button
                type="button"
                onClick={handleQuickSummaryClick}
                className="rounded-full bg-primary-600 px-3 py-1.5 text-xs font-bold text-white shadow-lg shadow-primary-900/30 transition hover:bg-primary-500"
              >
                {copy.summaryButton}
              </button>
            )}

            {props.onRemove && (
              <button
                type="button"
                onClick={handleRemoveClick}
                className="rounded-full bg-black/65 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-black/80"
              >
                {copy.remove}
              </button>
            )}
          </div>
        </div>
      </div>

      {quickSummaryModal}
    </>
  );
};
